import React from "react";
import { Button } from "antd";
import { PricingPlanProps } from "../../types/PricingPlanProps";

type UILang = "en" | "he";

const PRICING_TR = {
  en: {
    title: "Pricing plans",
    change_phone: "Change phone number",
    guidance_p1: "Choose the plan that fits your expected messaging volume and feature needs.",
    guidance_p2: "Each plan defines two message quotas: Business Initiated messages (campaigns, reminders, automations your business sends first) and chat conversation messages (replies within customer-initiated threads, including AI Agent bot replies). Both quotas vary across plans — higher plans include more of each.",
    guidance_p3: "Premium features such as AI Agent are only available on select plans. Review each plan's feature list carefully before choosing.",
    per_month: "$ per month",
    unlimited: (cost: number) => `Unlimited messages at $${cost} each`,
    free_credit: (n: number) => `${n} Business Initiated messages / month`,
    analytics: "Analytics & Reports",
    support: "Support Widget",
    automations: "Automations",
    campaigns: "Campaigns",
    ai_agent: "AI Agent",
    live_chat: "Live Chat with Clients",
    mobile_app: "Mobile Chat App",
    black_periods: "Black Periods",
    update_plan: "Update Plan",
  },
  he: {
    title: "תוכניות מחיר",
    change_phone: "שנה מספר טלפון",
    guidance_p1: "בחרו את התוכנית המתאימה לנפח ההודעות הצפוי ולצרכי הפיצ'רים שלכם.",
    guidance_p2: "כל תוכנית מגדירה שתי מכסות הודעות: הודעות Business Initiated (קמפיינים, תזכורות ואוטומציות שהעסק שולח ראשון) והודעות שיחת צ'אט (תגובות בתוך שיחות שלקוחות פתחו, כולל תגובות בוט של סוכן ה-AI). שתי המכסות משתנות בין תוכניות — תוכניות גבוהות כוללות יותר מכל אחת.",
    guidance_p3: "פיצ'רים פרמיום כמו סוכן AI זמינים רק בתוכניות נבחרות. בדקו היטב את רשימת הפיצ'רים של כל תוכנית לפני הבחירה.",
    per_month: "$ לחודש",
    unlimited: (cost: number) => `הודעות ללא הגבלה — $${cost} לכל הודעה`,
    free_credit: (n: number) => `${n} הודעות Business Initiated לחודש`,
    analytics: "ניתוח נתונים ודוחות",
    support: "ווידג'ט תמיכה",
    automations: "אוטומציות",
    campaigns: "קמפיינים",
    ai_agent: "סוכן AI",
    live_chat: "צ'אט חי עם לקוחות",
    mobile_app: "אפליקציית צ'אט נייד",
    black_periods: "תקופות שקט",
    update_plan: "עדכן תוכנית",
  },
};

type Props = {
  lang?: UILang;
  stepNumber?: number;
  isVisible: boolean;
  isLast: boolean;
  pricingPlans: PricingPlanProps[] | null;
  hasPlan: boolean;
  pricingPlanId?: number;
  onSelectPlan: (planId: number) => void;
  onChangePhone?: () => void;
};

const Step1PricingFlowBubble = ({
  lang = "en",
  stepNumber = 1,
  isVisible,
  isLast,
  pricingPlans,
  hasPlan,
  pricingPlanId,
  onSelectPlan,
  onChangePhone,
}: Props) => {
  const tr = PRICING_TR[lang];
  if (!isVisible) return null;

  return (
    <>
      <div className="v2oa-step v2oa-assistant">
        <div className="v2oa-bubble" dir={lang === "he" ? "rtl" : "ltr"}>
          <div className="pricing-plan-steps">
            <div className="v2oa-step-title"><span>{stepNumber}</span>{tr.title}</div>
            <div className="pricing-plan-guidance">
              <div>{tr.guidance_p1}</div>
              <div>{tr.guidance_p2}</div>
              <div>{tr.guidance_p3}</div>
            </div>
            {onChangePhone ? (
              <Button type="link" size="small" onClick={onChangePhone} className="pricing-change-phone-btn">
                {tr.change_phone}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="copilot-pricing-row">
        {pricingPlans?.filter(plan => plan.isV2)?.map((plan, idx) => {
          const themeIndex = (idx % 4) + 1;
          const isSelected = Boolean(hasPlan && pricingPlanId === plan.id);
          const dimmed = Boolean(hasPlan && pricingPlanId && pricingPlanId !== plan.id);
          return (
            <div
              key={plan.id}
              className={`copilot-pricing-card${dimmed ? " copilot-pricing-card-dim" : ""}${isSelected ? " copilot-pricing-card-selected" : ""}`}
            >
              <div className={`v2oa-step copilot-pricing-card-inner copilot-plan-theme-${themeIndex}`}>
                <div className="copilot-pricing-plan-title">{plan.title}</div>
                <div className="copilot-pricing-plan-fee">
                  {plan.fee}
                  <span>{tr.per_month}</span>
                </div>
                <div className="features">
                  <div className="feature checked">{plan.allowExtraCharge ? tr.unlimited(plan.messageCost) : tr.free_credit(plan.freeCreditPerCycle ?? 0)}</div>
                  <div className="feature checked">{tr.analytics}</div>
                  <div className="feature checked">{tr.support}</div>
                  <div className="feature checked">{tr.automations}</div>
                  <div className="feature checked">{tr.campaigns}</div>
                  <div className={`feature ${plan.withAI ? "checked" : ""}`}>{tr.ai_agent}</div>
                  <div className="feature checked">{tr.live_chat}</div>
                  <div className="feature checked">{tr.mobile_app}</div>
                  <div className="feature checked">{tr.black_periods}</div>
                </div>
                <Button
                  type="primary"
                  disabled={!isLast}
                  onClick={() => onSelectPlan(plan.id)}
                >
                  {lang === "he" ? tr.update_plan : (plan.cta || tr.update_plan)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Step1PricingFlowBubble;
